// Search Console — domain RPC module (V6).
//
// Built per the Search Console V6 spec (§A.1), modelled on `src/api/conversation.js`:
//   - Import the singleton client from `../lib/supabase` (there is no
//     `src/api/client.js` in this repo).
//   - Raw return + throw. The wrappers return the RPC's own `data` and throw a
//     labelled Error on failure — NOT the legacy `{error, data, message}` envelope
//     that `auth.js`'s `wrapSupabase` produced. Call sites read the payload
//     directly (`resp.panels`, not `resp.data.panels`).
//   - Every call checks the RPC's own `{error:true, message}` self-gate and throws,
//     so a per-promise `.catch` fires and `PanelErrorBoundary` renders a friendly
//     message.
//   - Errors are labelled with the call name (the lesson at api/auth.js:11–14).
//
// This REPLACES the GSC block that lived in `auth.js` (the legacy-envelope
// `gscRpc`). Nothing GSC remains in `auth.js` (§A: "Nothing is added to
// src/api/auth.js").

import { supabase } from '../lib/supabase';

// ─── Core RPC helper ────────────────────────────────────────────────────────
// Ported from conversation.js's `conversationRpc`. A refused HTTP/2 stream is a
// transport failure, not a database one; these are idempotent reads, so a
// transport failure is retried after a short jittered backoff while a Postgres
// error, an RLS refusal or a bad parameter fails on the first attempt.
const TRANSPORT_FAILURE = /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const RPC_RETRY_DELAYS_MS = [200, 600];
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const jitter = (ms) => ms + Math.floor(Math.random() * 250);

const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || '');

/**
 * Call a Supabase RPC, throw a labelled error on failure (a Supabase error OR the
 * RPC's own `{error:true, message}` self-gate), and return the raw `data`.
 *
 * @param {string} name the `fn_*` RPC name
 * @param {object} params the `p_*` parameter map
 * @param {string} [label] a short label prefixed onto any thrown message
 * @returns {Promise<*>} the RPC's raw `data`
 */
export async function gscRpc(name, params, label) {
  // try/catch, not `.catch()` on the builder: supabase.rpc() returns a thenable
  // PostgrestFilterBuilder that has `then` but no `catch`; calling .catch on it
  // throws before the request is issued.
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  for (const ms of RPC_RETRY_DELAYS_MS) {
    if (!isTransportFailure(result.error)) break;
    // eslint-disable-next-line no-await-in-loop
    await sleep(jitter(ms));
    // eslint-disable-next-line no-await-in-loop
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || 'Supabase RPC failed';
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  // The RPC gates itself and returns `{error:true, message}` rather than throwing;
  // surface that as a real error so the caller's `.catch` path fires.
  if (data && typeof data === 'object' && data.error === true) {
    const detail = data.message || `${name} returned an error`;
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  return data;
}

// ─── Page envelope ──────────────────────────────────────────────────────────

// fn_get_search_console — the one envelope. Returns every panel with its sentence
// and the top-level verdict joined server-side; there is no separate sentences
// read. `p_as_of` is OMITTED for the latest snapshot rather than passed as null:
// the function's default is what selects "latest", and an explicit null is a
// different argument list (verified live — see the retired auth.js block).
export const getSearchConsole = (clientId, asOf = null) =>
  gscRpc(
    'fn_get_search_console',
    asOf ? { p_client_id: clientId, p_as_of: asOf } : { p_client_id: clientId },
    'page',
  );

// fn_get_search_console_dates → { snapshot_dates: [...], detail_from, detail_to }.
// Populates the as-of picker and bounds the Detail range control.
export const getSearchConsoleDates = (clientId) =>
  gscRpc('fn_get_search_console_dates', { p_client_id: clientId }, 'dates');

// ─── The drill function (§A.6) ──────────────────────────────────────────────
// fn_drill(p_what, p_id, p_scope, p_limit). Called with a row's own `drill`
// object — `drill.value` and `drill.id` — never a constructed one. `p_scope`
// carries the page's live filters `{client_id, start_date, end_date, search_type}`
// so a drill never loses the reader's context. Shared with Conversation
// Intelligence, which sends non-`gsc.` `p_what` values this page never sends.
export const drill = (what, id, scope, limit = 100) =>
  gscRpc('fn_drill', { p_what: what, p_id: id, p_scope: scope, p_limit: limit }, 'drill');

// ─── The five paginated RPCs (§A.7) ─────────────────────────────────────────
// All return { summary, items, pagination } and fire ON DEMAND from an expanded
// panel or a drill — never on page load. Ergonomic (clientId, opts) signatures
// mirroring conversation.js; the params are built here so call sites stay narrow.

export const getQueries = (clientId, { startDate, endDate, searchType, search, page, limit, exact } = {}) =>
  gscRpc('fn_get_gsc_queries', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_search_type: searchType ?? 'web',
    p_search: search ?? null,
    p_page: page ?? 1,
    p_limit: limit ?? 50,
    p_exact: exact ?? false,
  }, 'queries');

export const getPages = (clientId, { startDate, endDate, searchType, search, page, limit, exact } = {}) =>
  gscRpc('fn_get_gsc_pages', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_search_type: searchType ?? 'web',
    p_search: search ?? null,
    p_page: page ?? 1,
    p_limit: limit ?? 50,
    p_exact: exact ?? false,
  }, 'pages');

// `p_query` / `p_page_url` drive the bidirectional filter on the query→page map;
// `p_exact` switches from substring to equality — sent true when a row is CLICKED.
export const getQueryPages = (clientId, { startDate, endDate, query, pageUrl, page, limit, exact } = {}) =>
  gscRpc('fn_get_gsc_query_pages', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_query: query ?? null,
    p_page_url: pageUrl ?? null,
    p_page: page ?? 1,
    p_limit: limit ?? 50,
    p_exact: exact ?? false,
  }, 'query_pages');

// The two health tables are current-state, not time series — neither takes a date
// range. Same {summary, items, pagination} shape as the three above.
export const getInspection = (clientId, { verdict, search, page, limit } = {}) =>
  gscRpc('fn_get_gsc_url_inspection', {
    p_client_id: clientId,
    p_verdict: verdict ?? null,
    p_search: search ?? null,
    p_page: page ?? 1,
    p_limit: limit ?? 50,
  }, 'inspection');

export const getSitemaps = (clientId, { page, limit } = {}) =>
  gscRpc('fn_get_gsc_sitemaps', {
    p_client_id: clientId,
    p_page: page ?? 1,
    p_limit: limit ?? 50,
  }, 'sitemaps');

// Position distribution — four bands (1-3, 4-10, 11-20, 21+), each with a query
// count, impressions, clicks and share, plus the same bands by month in `trend`.
// The page envelope already carries this as the `position_bands` panel; call this
// directly only when the Detail range differs from the page window (Part F).
export const getBands = (clientId, { startDate, endDate, searchType } = {}) =>
  gscRpc('fn_get_gsc_position_bands', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_search_type: searchType ?? 'web',
  }, 'bands');

// Per-row sparklines. `p_values` is THE EXACT ROWS ON SCREEN — one call backs a
// whole table page. Returns { kind, series } keyed by query or page.
export const getSeries = (clientId, { startDate, endDate, kind, values, searchType } = {}) =>
  gscRpc('fn_get_gsc_series', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_kind: kind ?? 'query',
    p_values: Array.isArray(values) ? values : [],
    p_search_type: searchType ?? 'web',
  }, 'series');
